import classNames from 'classnames';
import * as df from 'date-fns';
import { camelCase } from 'lodash-es';
import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PaymentMethod } from '../../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../../interfaces/merchants/resources/books/inputs';
import BooksLabel from '../common/labels/BooksLabel';
import GoogleCalendarTemplateUrl from '../common/texts/GoogleCalendarTemplateUrl';
import BooksMenuMerchant from '../BooksMenuMerchant';

import css from './BooksEventItemList.module.scss';

interface Props {
  nickname?: string;
  resourceName?: string;
  resourceId?: string;
  resourceImage?: string;
  resourceUrl?: string;
  payment?: PaymentMethod['method'];
  eventTimeSlot: IInputs.EventTimeSlot;
  attendees: number;
  price?: string;
  googleCalendarTemplateUrl?: string;
}

const BooksEventItemList = ({
  eventTimeSlot,
  attendees,
  price,
  payment,
  googleCalendarTemplateUrl,
  ...props
}: Props) => {
  const classes = classNames(css.BooksEventItemList);
  const intl = useIntl();

  return (
    <div className={classes}>
      <BooksMenuMerchant
        image={props.resourceImage}
        name={props.resourceName}
        nickname={props.nickname}
        url={props.resourceUrl}
      />
      <dl className={css.BooksEventItemList__List}>
        {attendees > 1 && (
          <>
            <dt className={css.BooksEventItemList__ListTitle}>
              {intl.formatMessage({
                id: 'books.booksMenu.numberOfBookedPeople',
              })}
            </dt>
            <dd className={css.BooksEventItemList__ListData}>
              <FormattedMessage
                id="books.booksMenu.attendees"
                values={{ attendees }}
              />
            </dd>
          </>
        )}
        <dt className={css.BooksEventItemList__ListTitle}>
          {intl.formatMessage({ id: 'books.booksMenu.bookDate' })}
        </dt>
        <dd className={css.BooksEventItemList__ListData} data-style="date">
          {eventTimeSlot.timeSlots.map((t, i) => (
            <time className={css.BooksEventItemList__ListDate} key={i}>
              {intl.formatDate(df.format(t.date), {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'short',
              })}
              <span className={css.BooksEventItemList__Time}>
                {t.startTime}
                <span> - </span>
                {t.endTime}
              </span>
            </time>
          ))}
          {googleCalendarTemplateUrl && (
            <GoogleCalendarTemplateUrl
              googleCalendarTemplateUrl={googleCalendarTemplateUrl}
            />
          )}
        </dd>
        {price && parseFloat(price) > 0 && (
          <>
            <dt className={css.BooksEventItemList__ListTitle}>
              {intl.formatMessage({ id: 'books.booksMenu.totalPrice' })}
            </dt>
            <dd className={css.BooksEventItemList__ListData} data-style="price">
              {price}円
            </dd>
            {payment && payment !== 'unspecified' && (
              <>
                <dt className={css.BooksEventItemList__ListTitle}>
                  {intl.formatMessage({
                    id: 'books.booksMenu.paymentMethod',
                  })}
                </dt>
                <dd className={css.BooksEventItemList__ListData}>
                  {payment === 'credit_card' && (
                    <span className={css.BooksEventItemList__ListTag}>
                      <BooksLabel
                        label={intl.formatMessage({
                          id: 'books.paymentMethod.prepayment',
                        })}
                      />
                    </span>
                  )}
                  {intl.formatMessage({
                    id: `books.paymentMethod.${camelCase(payment)}`,
                  })}
                </dd>
              </>
            )}
          </>
        )}
      </dl>
    </div>
  );
};

export default BooksEventItemList;
